import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import { tv, VariantProps } from "tailwind-variants"

import { cn } from "@/utils/styles"

const popoverVariants = tv({
  slots: {
    overlay: [
      "fixed inset-0 z-50",
      "bg-transparent",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
    ],
    trigger: [],
    content: [
      "bg-popover text-popover-foreground",
      "data-[state=open]:animate-in data-[state=closed]:animate-outw",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
      "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
      "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      "z-50 rounded-md border shadow-md outline-none",
    ],
    itemButton: [
      "flex w-full flex-1 items-center justify-start",
      "px-4 py-2",
      "text-gray-10",
      "typo-body-3 font-medium",
      "hover:bg-gray-02",
      "focus-visible:outline-none focus-visible:ring-ring",
      "disabled:text-gray-05",
    ],
  },
})
export type PopoverVariantProps = VariantProps<typeof popoverVariants>

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverPortal = PopoverPrimitive.Portal

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    matchedWidth?: boolean
  }
>(
  (
    { className, align = "center", sideOffset = 4, matchedWidth = false, ...props },
    ref,
  ) => {
    const variants = popoverVariants()
    return (
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(variants.content(), className, {
          "w-[--radix-popover-trigger-width]": matchedWidth,
        })}
        {...props}
      />
    )
  },
)
PopoverContent.displayName = PopoverPrimitive.Content.displayName

const PopoverItemButton = React.forwardRef<
  React.ElementRef<"button">,
  React.ComponentPropsWithoutRef<"button">
>(({ className, ...props }, ref) => {
  const variants = popoverVariants()

  return (
    <button
      type="button"
      ref={ref}
      className={cn(variants.itemButton(), className)}
      role="menuitem"
      {...props}
    />
  )
})
PopoverItemButton.displayName = "PopoverItemButton"

export { Popover, PopoverContent, PopoverItemButton, PopoverPortal, PopoverTrigger }
